import { InfoCircleOutlined, UploadOutlined } from '@ant-design/icons'
import { Table, Upload } from 'antd'
import { UploadFile } from 'antd/lib/upload/interface'
import React, { useCallback, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import styled from 'styled-components'
import { Button } from 'sutra/components/Button'
import { FormItem } from 'sutra/components/Form'
import { Input } from 'sutra/components/Input/Input'
import { Select } from 'sutra/components/Select/Select'
import { Tooltip } from 'sutra/components/Tooltip/Tooltip'

import { FormikType } from 'features/project-form/features/source/components/NewSource'
import {
  CUSTOM_FIELD_NAMES,
  CustomFieldName,
  remapCustomFields,
} from 'features/project-form/features/source/components/sources/custom-field-mappings'
import { getFieldOptionsFromSourceData } from 'features/project-form/features/source/utils'

type Props = {
  formik: FormikType
}

type SelectItemProps = {
  fieldOptions: Array<string>
  onChange: (p: string | undefined) => void
  isSelected: (f: string) => boolean
  value?: string
  fields: { [s: string]: string }
}

const fields: { [s: string]: string } = {
  Body: 'body',
  Title: 'title',
  Id: 'input_id',
  Username: 'username',
  'User ID': 'userid',
  Location: 'location',
  Country: 'country',
  Date: 'create_time',
  Rating: 'score',
  'Syndicated From': 'syndicated_from',
  Source: 'source',
  Item: 'product',
  Product: 'product_hierarchy',
  'Formal Product': 'formal_product',
  Brand: 'brand',
  URL: 'url',
  Promotion: 'promotion',
  Verified: 'verified',
  Recommended: 'recommended',
  'Post Custom field': 'custom',
  'Source Custom field': 'source_custom',
  'Product Custom field': 'product_custom',
}

const SelectItem = React.memo(function SelectItem({
  fieldOptions,
  value,
  fields,
  onChange,
  isSelected,
}: SelectItemProps) {
  const selectedField = Object.keys(fields).find((f) => fields[f] === value)
  return (
    <DataItem>
      <Select
        value={selectedField ?? '-1'}
        onChange={(v) => onChange(v !== '-1' ? v : undefined)}
        options={[
          { value: '-1', label: '[Ignored]' },
          ...fieldOptions.map((option) => ({
            value: option,
            label: option,
            disabled: isSelected(option),
          })),
        ]}
      />
    </DataItem>
  )
})

export const File: React.FC<Props> = ({ formik }) => {
  const [fileList, setFileList] = useState<UploadFile[]>([])
  const [options, setOptions] = useState<string[]>()
  const [values, setValues] = useState<string[][]>()

  const [tableColumns, setTableColumns] = useState<
    Array<{
      title: string
      dataIndex: number
      key: number
      render: (t: string | null) => React.ReactNode
    }>
  >()
  const [tableValues, setTableValues] =
    useState<Record<number, string | null>[]>()

  const isSelected = useCallback(
    (fieldName: string) => {
      const field = fields[fieldName]
      if (!field) return false
      const meta = formik.values as Record<string, unknown>

      if (['custom', 'source_custom', 'product_custom'].includes(field))
        return false
      return ![undefined, null].includes(meta[field] as null | undefined)
    },
    [formik.values],
  )

  useEffect(() => {
    if (fileList && fileList.length) {
      const file = fileList[0]
      if (!file) return

      getFieldOptionsFromSourceData(file, (o: string[], v: string[][]) => {
        setOptions(o)
        setValues(v)
      })
    }
  }, [fileList])

  useEffect(() => {
    if (formik.values.sourceFile && !fileList.length) {
      setFileList([formik.values.sourceFile as unknown as UploadFile])
    }
  }, [formik.values.sourceFile, fileList.length])

  useEffect(() => {
    if (options && values) {
      const columns: Array<{
        title: string
        dataIndex: number
        key: number
        render: (t: string | null) => React.ReactNode
      }> = []
      options.forEach((option, index) => {
        columns.push({
          title: option,
          dataIndex: index,
          key: index,
          render: (t: string | null) => {
            if (t !== null) return t
            const formValues = formik.values as Record<string, unknown>
            const value = Object.keys(formik.values)
              .map((key) => ({ key: key, value: formValues[key] }))
              .find(({ value }) => parseInt(String(value)) === index)

            return (
              <SelectItem
                isSelected={isSelected}
                fieldOptions={Object.keys(fields)}
                fields={fields}
                value={value?.key}
                onChange={(v: string | undefined) => {
                  const metaObject = formik.values as Record<string, unknown>
                  const oldFieldName = Object.keys(metaObject).find(
                    (key) => metaObject[key] === index,
                  )
                  formik.setFieldValue(`${oldFieldName}`, undefined)

                  const fieldName = v ? fields[v] : undefined
                  const isCustomField = CUSTOM_FIELD_NAMES.includes(
                    fieldName as CustomFieldName,
                  )
                  const customFieldUpdates = remapCustomFields(
                    formik.values,
                    index,
                    isCustomField
                      ? {
                          fieldName: fieldName as CustomFieldName,
                          columnName: option,
                        }
                      : undefined,
                  )
                  Object.entries(customFieldUpdates).forEach(
                    ([customFieldName, customFields]) => {
                      formik.setFieldValue(customFieldName, customFields)
                    },
                  )

                  if (fieldName && !isCustomField) {
                    formik.setFieldValue(`${fieldName}`, index)
                  }
                }}
              />
            )
          },
        })
      })
      setTableColumns(columns)

      const resultValues: Array<Record<number, string | null>> = []
      resultValues.push(
        columns.reduce(
          (acc, _, i) => ({ ...acc, [i]: null }),
          {} as Record<number, string | null>,
        ),
      )
      values.forEach((valueList) => {
        const o: Record<number, string> = {}
        valueList.forEach((value, i) => {
          o[i] = value.length > 100 ? value.substring(0, 100) : value
        })
        resultValues.push(o)
      })
      setTableValues(resultValues)
    }
  }, [formik.values, options, values, formik, isSelected])

  const { touched, errors } = formik

  const sourceTitleError = touched.sourceTitle && errors.sourceTitle

  return (
    <>
      <FormItem
        label={
          <Tooltip content="Select the file you'd like to upload into Yogi. Ensure that the top row holds the relevant column names. Also please note that Yogi only supports files with up to 50,000 rows. Any files longer that will not be allowed.">
            <span>
              File <InfoCircleOutlined />
            </span>
          </Tooltip>
        }
        validateStatus={errors.sourceFile && 'error'}
        help={formik.submitCount > 0 && errors.sourceFile}
        name="photos"
      >
        <Upload
          fileList={fileList}
          accept={'.csv'}
          beforeUpload={() => false}
          onChange={(info) => {
            const file = info.file as {
              status?: string
              size: number
              name: string
              uid?: string
            }
            if (file.status === 'removed') {
              formik.setFieldValue('sourceFile', null)
              formik.setFieldValue('sourceTitle', null)
              setFileList([])
              setTableColumns(undefined)
              setTableValues(undefined)
            } else {
              if (file.size / 1000 / 1000 >= 600) {
                toast.error('The uploaded file should be less than 600 MB')
                return
              }
              formik.setFieldValue('sourceFile', file)
              formik.setFieldValue('sourceTitle', file.name.split('.')[0])
              setFileList([file as UploadFile])
            }
          }}
        >
          <Button variant="secondary">
            <UploadOutlined /> Click to Upload
          </Button>
        </Upload>
      </FormItem>
      <FormItem
        label={
          <Tooltip content="Give your file a name to display in the charts. Make sure it's one you'll recognize.">
            <span>
              Source Title <InfoCircleOutlined />
            </span>
          </Tooltip>
        }
        validateStatus={sourceTitleError ? 'error' : ''}
        help={sourceTitleError}
        wrapperCol={{ span: 10 }}
      >
        <Input
          name="sourceTitle"
          onChange={formik.handleChange}
          value={formik.values.sourceTitle}
          $error={Boolean(sourceTitleError)}
          placeholder="Insert title here"
        />
      </FormItem>
      <Error>{errors?.title}</Error>
      <TableWrapper>
        {tableColumns && tableValues && (
          <Table
            scroll={{ x: 400 }}
            dataSource={tableValues}
            columns={tableColumns}
            pagination={false}
          />
        )}
      </TableWrapper>
    </>
  )
}
const DataItem = styled.div`
  width: 180px;
  margin-bottom: 8px;
`
const TableWrapper = styled.div`
  max-width: 100%;
  min-height: 300px;
  position: relative;

  td {
    min-width: 50px;
    max-width: 300px;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .ant-table-thead > tr > th,
  .ant-table-tbody > tr > td {
    padding: 6px;
    white-space: nowrap;
  }
`
const Error = styled.div`
  text-align: center;
  color: #ff4d4f;
`
